import { useField, useFormikContext, getIn } from 'formik';
import React__default, { useState, useCallback } from 'react';
import { debounce, throttle } from 'throttle-debounce';
import { setDisabledDataAttribute } from '../inputs/InputCommon.js';
import { TextInput } from '../inputs/TextInput.js';
import { Stack } from '../layout/Stack.js';
import { setErrorDataAttribute, FieldErrorMessage } from './FieldError.js';

function isNumberField(value, type) {
    return type === "number" || type === "range";
}
function isTextField(value, type) {
    return type === "email" || type === "hidden" || type === "password" || type === "tel" || type === "text" || type === "url";
}
function isNumberOnFieldChange(onFieldChange, type) {
    return type === "number" || type === "range";
}
function isTextOnFieldChange(onFieldChange, type) {
    return type === "email" || type === "hidden" || type === "password" || type === "tel" || type === "text" || type === "url";
}
function isNumberOnFieldBlur(onFieldBlur, type) {
    return type === "number" || type === "range";
}
function isTextOnFieldBlur(onFieldBlur, type) {
    return type === "email" || type === "hidden" || type === "password" || type === "tel" || type === "text" || type === "url";
}
const getFieldValueByKey = (values, key)=>{
    // Keys may be of the form 'primarykey.subkey.key' to access
    // nested value properties.
    const value = getIn(values, key);
    return value;
};
const useTextFieldThrottle = (fieldName, type, onFieldChange = ()=>{}, onFieldBlur = ()=>{}, throttleMS = 0)=>{
    const { setFieldTouched , setFieldValue , values  } = useFormikContext();
    const [focused, setFocused] = useState(false);
    const [value, setValue] = useState(undefined);
    // determine whether we should throttle
    const isThrottledField = typeof throttleMS === "number" && throttleMS > 50;
    const debouncedHandleChange = useCallback((value)=>{
        isThrottledField ? debounce(throttleMS, setFieldValue)(fieldName, value) : setFieldValue(fieldName, value);
    }, [
        isThrottledField,
        throttleMS
    ]);
    const throttledOnFieldChange = useCallback(isThrottledField ? throttle(throttleMS, false, !!onFieldChange ? onFieldChange : ()=>{}) : onFieldChange, [
        onFieldChange,
        isThrottledField,
        throttleMS
    ]);
    const onChange = useCallback((value)=>{
        if (isTextField(value, type) && isTextOnFieldChange(throttledOnFieldChange, type)) {
            setValue(value);
            debouncedHandleChange(value);
            if (!!onFieldChange) {
                throttledOnFieldChange(value);
            }
        }
        if (isNumberField(value, type) && isNumberOnFieldChange(throttledOnFieldChange, type)) {
            const parsed = parseFloat(value);
            let val = parsed;
            if (isNaN(parsed)) {
                val = "";
            }
            setValue(val);
            debouncedHandleChange(val);
            if (!!onFieldChange) {
                throttledOnFieldChange(val);
            }
        }
    }, [
        type,
        debouncedHandleChange,
        onFieldChange,
        throttledOnFieldChange
    ]);
    const onFocus = useCallback(()=>{
        setFocused(true);
        setValue(values[fieldName]);
    }, [
        values,
        fieldName
    ]);
    const onBlur = useCallback((value)=>{
        setFocused(true);
        if (isNumberField(value, type) && isNumberOnFieldBlur(onFieldBlur, type)) {
            const parsed = parseFloat(value.toString());
            let val = parsed;
            if (isNaN(parsed)) {
                val = "";
            }
            setValue(val);
            if (!!onFieldBlur) {
                onFieldBlur(val);
            }
        }
        if (isTextField(value, type) && isTextOnFieldBlur(onFieldBlur, type)) {
            const trimmedValue = value.trim();
            setValue(trimmedValue);
            if (!!onFieldBlur) {
                onFieldBlur(trimmedValue);
            }
        }
        setFieldTouched(fieldName);
    }, [
        type,
        onFieldBlur,
        setFieldTouched,
        fieldName
    ]);
    return {
        onChange,
        onBlur,
        onFocus,
        value: focused && isThrottledField ? value : getFieldValueByKey(values, fieldName)
    };
};
/**
 * Text Input with Formik Field bindings.
 * Accepts custom + all standard text input props except for `placeholder` - label should be provided instead
 *
 * @example
 * <TextField name="notes" label={'Make a note...'} />
 */ const TextField = ({ children , disabled =false , error , icon , name , reserveMessageSpace =true , throttleMS , onFieldChange , onFieldBlur , type ="text" , ...props })=>{
    const [field, meta] = useField(name);
    const fieldThrottle = useTextFieldThrottle(name, type, onFieldChange, onFieldBlur, throttleMS);
    const inputProps = {
        type,
        value: fieldThrottle.value,
        onBlur: fieldThrottle.onBlur,
        onChange: fieldThrottle.onChange,
        onFocus: fieldThrottle.onFocus,
        step: props.step
    };
    return React__default.createElement(Stack, {
        "data-hpy-web-text-field": true,
        ...!!icon && {
            "data-hpy-web-text-input-has-icon": !!icon
        },
        ...setDisabledDataAttribute(disabled),
        ...setErrorDataAttribute(meta.touched && !!meta.error || !!error),
        gap: 0.25
    }, React__default.createElement(TextInput, {
        ...props,
        ...field,
        ...inputProps,
        disabled: disabled,
        hasError: meta.touched && !!meta.error || !!error,
        icon: icon
    }), children, React__default.createElement(FieldErrorMessage, {
        name: name,
        reserveMessageSpace: reserveMessageSpace,
        error: error
    }));
};
TextField.dataId = "data-hpy-web-text-field";
TextField.displayName = "TextField";

export { TextField };
